import GuestAITestConversation from "./components/GuestAITestConversation";
import "./SettingsTrainingData.css";
import { PackageUpgradeModal } from "./components/OfferGate";
import React, { useEffect, useState } from "react";
import {
  Box,
  Header,
  Heading,
  Text,
  TextArea,
  Button,
  CheckBox,
  Tabs,
  Tab,
} from "grommet";
import { Magic } from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { HotelService, TrainingDataService } from "../../services";
import { useFetchHotel } from "./redux/fetchHotel";
import { useSetCurrentUser } from "./redux/hooks";
import { dismissLoading, presentLoading } from "@features/shared/Loading";
import TrainingDataList from "./components/TrainingDataList";
import KnowledgeBaseUpload from "./components/KnowledgeBaseUpload";
import KnowledgeBaseList from "./components/KnowledgeBaseList";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

// Lets other pages deep-link into a specific tab, e.g. /settings/training?tab=qna
const TAB_PARAM_TO_INDEX = { knowledge: 0, qna: 1, instructions: 2 };

export default function SettingsTrainingData() {
  const { t } = useTranslation();
  const [upgrade, setUpgrade] = useState(false);
  const { hotel, fetchHotel } = useFetchHotel();
  const { isAdmin } = useSetCurrentUser();
  const [searchParams] = useSearchParams();
  const [aiInstructions, setAiInstructions] = useState(
    hotel?.aiInstructions || "",
  );
  const [activeTab, setActiveTab] = useState(
    () => TAB_PARAM_TO_INDEX[searchParams.get("tab")] ?? 0,
  );
  const [kbRefreshSignal, setKbRefreshSignal] = useState(0);
  const [qaRefreshSignal, setQaRefreshSignal] = useState(0);

  useEffect(() => {
    setAiInstructions(hotel?.aiInstructions || "");
  }, [hotel?.aiInstructions]);

  const handleKbUploaded = () => setKbRefreshSignal((s) => s + 1);

  const handleToggleGuestAssistant = ({ target: { checked } }) => {
    if (checked && !hotel?.aiEnabled) {
      setUpgrade(true);
      return;
    }
    presentLoading();
    HotelService.updateHotel({ aiGuestAssistantEnabled: checked }).then(
      () => {
        presentToastSuccess(
          checked
            ? t("settingsTraining.assistantActivated")
            : t("settingsTraining.assistantDeactivated"),
        );
        fetchHotel();
        dismissLoading();
      },
      (error) => {
        presentToastErrorContent(error);
        dismissLoading();
      },
    );
  };

  const handleCreateModel = () => {
    presentLoading();
    TrainingDataService.createModel(false).then(
      () => {
        presentToastSuccess(t("settingsTraining.savingModel"));
        setQaRefreshSignal((s) => s + 1);
        dismissLoading();
      },
      (error) => {
        presentToastErrorContent(error);
        dismissLoading();
      },
    );
  };

  const handleSaveInstructions = () => {
    presentLoading();
    HotelService.updateHotel({ aiInstructions }).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess(t("settingsTraining.instructionsUpdated"));
        } else {
          presentToastSuccess(t("settingsTraining.nothingToChange"));
        }
        dismissLoading();
      },
      (error) => {
        presentToastErrorContent(error);
        dismissLoading();
      },
    );
  };

  return (
    <div className="ai-training-layout">
      <Box
        className="ai-training-content"
        flex={false}
        gap="medium"
        justify="start"
        width="100%"
      >
        {upgrade && (
          <PackageUpgradeModal
            capability="aiGuest"
            onClose={() => setUpgrade(false)}
          />
        )}
        <Heading alignSelf="start" level="2">
          {t("settingsTraining.guestsTitle")}
        </Heading>

        {isAdmin && (
          <Box
            background="light-2"
            round
            pad="medium"
            gap="xsmall"
            border={{
              color: hotel?.aiGuestAssistantEnabled ? "status-ok" : "border",
            }}
          >
            <CheckBox
              checked={!!hotel?.aiGuestAssistantEnabled}
              onChange={handleToggleGuestAssistant}
              label={t("settingsTraining.activateAssistant")}
            />
            <Text size="small" color="dark-4">
              {t("settingsTraining.activateAssistantHelp")}
            </Text>
          </Box>
        )}

        <Tabs activeIndex={activeTab} onActive={setActiveTab}>
          {/* ── Tab 1: Knowledge Base ──────────────────────────────────── */}
          <Tab title={t("settingsTraining.tabKnowledgeBase")}>
            <Box gap="large" pad={{ top: "medium" }}>
              <KnowledgeBaseUpload onUploaded={handleKbUploaded} />
              <KnowledgeBaseList refreshSignal={kbRefreshSignal} />
            </Box>
          </Tab>

          {/* ── Tab 2: Questions & Answers ─────────────────────────────── */}
          <Tab title={t("settingsTraining.tabQnA")}>
            <Box gap="medium" pad={{ top: "medium" }}>
              <Header
                align="center"
                direction="row"
                justify="center"
                gap="medium"
                fill="horizontal"
              >
                <Button
                  icon={<Magic size="small" />}
                  label={t("settingsTraining.saveModel")}
                  primary
                  color="status-warning"
                  onClick={handleCreateModel}
                />
              </Header>
              <TrainingDataList
                internal={false}
                refreshSignal={qaRefreshSignal}
              />
            </Box>
          </Tab>

          {/* ── Tab 3: Custom Instructions ─────────────────────────────── */}
          <Tab title={t("settingsTraining.tabCustomInstructions")}>
            <Box gap="medium" pad={{ top: "medium" }} width="large">
              <Text size="small" color="dark-4">
                {t("settingsTraining.customInstructionsHelp")}
              </Text>
              <TextArea
                value={aiInstructions}
                onChange={({ target: { value } }) => setAiInstructions(value)}
                style={{ height: 120 }}
                maxLength={500}
                placeholder={t(
                  "settingsTraining.customInstructionsPlaceholder",
                )}
              />
              <Box align="end">
                <Button
                  label={t("settingsTraining.saveInstructions")}
                  primary
                  color="status-ok"
                  onClick={handleSaveInstructions}
                  size="small"
                />
              </Box>
            </Box>
          </Tab>
        </Tabs>
      </Box>
      <GuestAITestConversation key={hotel?._id} />
    </div>
  );
}
